import { capturePrivateSession, assertPrivateSessionLifetime, getPrivateSessionState, isPrivateSessionCurrent } from "@/lib/api/private-session";
import { useCallback, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@a2z/ui";
import { changePrivateSession } from "@/shared/privateSessionChange";
import { apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { prepareRestaurantNotificationLogout } from "@/lib/api/notifications";
import { firebaseSessionAdapter } from "@/lib/firebase/auth";
import { useErrorMessage } from "@/shared/errors";
import { queryKeys } from "@/shared/queries";
import type { SellerSession } from "@/lib/api/types";

/**
 * Sign out, from anywhere.
 *
 * Extracted from `ManageShell`, which owned the only copy — and that is exactly
 * why the kitchen board had no sign-out at all: the board renders OUTSIDE that
 * shell (it is a third surface, a screen on a wall), so a cook on a shared
 * tablet had no way to leave their session short of clearing cookies. A hook
 * rather than a second inline copy, so the two can never disagree about what
 * signing out means.
 *
 * The failure path is deliberate and unchanged: say what happened, but still
 * tear the local session down and leave. Stranding someone on a till they
 * believe they signed out of is worse than a failed request they can see.
 */
export function useLogout(): { logout: () => void; isLoggingOut: boolean } {
  const lifetime = useRef(capturePrivateSession()).current;
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const [isLoggingOut, setLoggingOut] = useState(false);

  const logout = useCallback(() => {
    assertPrivateSessionLifetime(lifetime);
    setLoggingOut(true);
    let transitionGeneration: number | undefined;
    void (async () => {
      // Owner-scoped token DELETEs need the seller cookie, so they must finish
      // before the logout request tears that cookie down. A failed DELETE keeps
      // only owner-tagged retry state; Firebase Auth is still cleared on the
      // shared restaurant device.
      const sellerId = queryClient.getQueryData<SellerSession>(
        queryKeys.session,
      )?.seller.id;
      await prepareRestaurantNotificationLogout(sellerId);
      await firebaseSessionAdapter.signOut().catch((failure) => {
        console.warn(
          "[restaurant-notifications] Firebase sign-out during logout failed",
          failure,
        );
      });
      assertPrivateSessionLifetime(lifetime);
      transitionGeneration = lifetime.generation + 1;
      await changePrivateSession(queryClient, () => apiFetch(endpoints.seller.logout, { method: "POST" }));
    })()
      .catch((failure) => {
        const ownsOutcome = transitionGeneration === undefined
          ? isPrivateSessionCurrent(lifetime)
          : getPrivateSessionState().generation === transitionGeneration;
        if (ownsOutcome && !(failure instanceof DOMException && failure.name === "AbortError")) toast.error(errorMessage(failure));
      })
      .finally(() => {
        if (transitionGeneration === undefined || getPrivateSessionState().generation !== transitionGeneration) return;
        queryClient.clear();
        // `?out=1`: the login screen says the device is ready for the next
        // person, so a shared till confirms the sign-out took.
        navigate(`/${locale}/login?out=1`, { replace: true });
      });
  }, [errorMessage, lifetime, locale, navigate, queryClient]);

  return { logout, isLoggingOut };
}
